// "Drift audits" (DC-10): a deployed DE's re-runs of the evals of the version it runs (monthly,
// after a target-system change, or on request). Shown on the DE profile (Agents › Overview) and in
// Operator › Deploys › a confirmed deploy, for company managers (the routes are manager-only)
// with the `agents` add-on. One call (`GET digital-employees/{id}/drift-audits`, newest first):
// the last result with its eval results, the history, and "Run audit now" (`POST … {trigger:
// manual}`). The worker runs a requested audit within a minute, so the list polls while one is
// scheduled or running. A failed audit opened a task on the supervisor seat; the row says so.
import { newIdempotencyKey, type OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Notice,
  SettingsSection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlayIcon, ShieldCheckIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { problemLines } from "../operator/templates/template-parts";
import { LoadError, Loading } from "../screen-parts";

export type DriftAudit = OperationOutput<"digitalEmployees.driftAudits">["data"][number];

/** Polling interval while an audit is scheduled or running (the worker ticks every minute). */
export const DRIFT_POLL_MS = 2000;

const open = (a: Pick<DriftAudit, "state">) => a.state === "scheduled" || a.state === "running";

export const driftAuditKeys = {
  all: () => ["drift-audits"] as const,
  list: (companyId: string, deId: string) => ["drift-audits", companyId, deId] as const,
};

export const driftAuditsQuery = (api: Api, companyId: string, deId: string) =>
  queryOptions({
    queryKey: driftAuditKeys.list(companyId, deId),
    queryFn: () =>
      api.client.digitalEmployees.driftAudits({
        params: { co: companyId, id: deId },
        query: { limit: 20 },
      }),
    refetchInterval: (q) => (q.state.data?.data.some(open) ? DRIFT_POLL_MS : false),
  });

export const DRIFT_STATE_LABEL: Record<DriftAudit["state"], string> = {
  scheduled: "Scheduled",
  running: "Running",
  passed: "Passed",
  failed: "Failed",
  error: "Could not run",
};
const STATE_VARIANT: Record<
  DriftAudit["state"],
  "neutral" | "info" | "success" | "danger" | "warning"
> = {
  scheduled: "neutral",
  running: "info",
  passed: "success",
  failed: "danger",
  error: "warning",
};
export const DRIFT_TRIGGER_LABEL: Record<DriftAudit["trigger"], string> = {
  schedule: "Monthly",
  target_changed: "Target system changed",
  manual: "Requested",
};

export function DriftStateBadge({ audit }: { audit: Pick<DriftAudit, "state"> }) {
  return (
    <Badge variant={STATE_VARIANT[audit.state]} data-slot="drift-state" data-state={audit.state}>
      {DRIFT_STATE_LABEL[audit.state]}
    </Badge>
  );
}

/** Eval results as "2 of 3 passed", or what the run left (none yet). */
function resultsText(a: DriftAudit): string {
  const keys = Object.keys(a.results);
  if (keys.length === 0) return open(a) ? "Waiting for the worker" : "No eval result";
  const passed = keys.filter((k) => a.results[k] === "pass").length;
  return `${passed} of ${keys.length} passed`;
}

/** Micros as money (the ledger posts the same figure in the company's currency). */
function costText(micros: number): string {
  if (micros <= 0) return "No cost";
  return `$${(micros / 1_000_000).toFixed(micros < 10_000 ? 4 : 2)}`;
}

export function DriftAuditsSection({
  api,
  companyId,
  deId,
  deployed,
}: {
  api: Api;
  companyId: string;
  deId: string;
  /** The DE runs a deployed version (the deploy mirror): only then can an audit be requested. */
  deployed: boolean;
}) {
  const qc = useQueryClient();
  const audits = useQuery(driftAuditsQuery(api, companyId, deId));
  const rows = audits.data?.data ?? [];
  const last = rows[0];
  const busy = rows.some(open);
  const run = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.requestDriftAudit({
        params: { co: companyId, id: deId },
        body: { trigger: "manual" },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: driftAuditKeys.list(companyId, deId) }),
  });
  return (
    <SettingsSection
      icon={ShieldCheckIcon}
      title="Drift audits"
      description="The evals of the version this DE runs, re-run monthly, after the target system changes, or on request. A failure opens a task for its supervisor."
      data-slot="drift-audits"
      actions={
        deployed ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || run.isPending}
            onClick={() => run.mutate()}
          >
            <PlayIcon aria-hidden="true" />
            {run.isPending ? "Requesting…" : busy ? "Audit in progress" : "Run audit now"}
          </Button>
        ) : null
      }
    >
      {run.isError ? (
        <Notice tone="danger" role="alert" className="mb-3">
          {problemLines(run.error, "The audit could not be requested.").join(" · ")}
        </Notice>
      ) : null}
      {audits.isPending ? (
        <Loading label="Loading drift audits" rows={2} />
      ) : audits.isError ? (
        <LoadError what="The drift audits" error={audits.error} />
      ) : !last ? (
        <p className="text-sm text-muted-foreground" data-slot="drift-empty">
          {deployed
            ? "No audit yet. The first runs within a month of the deploy, or now on request."
            : "Not deployed through AiGentPanel: there is no version to audit."}
        </p>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm" data-slot="drift-last">
            <span className="text-muted-foreground">Last audit</span>
            <DriftStateBadge audit={last} />
            <span>{resultsText(last)}</span>
            <span className="text-muted-foreground">
              · {DRIFT_TRIGGER_LABEL[last.trigger]} ·{" "}
              {formatDateTime(last.ended_at ?? last.started_at ?? last.scheduled_for)}
            </span>
          </div>
          {Object.keys(last.results).length > 0 ? (
            <ul className="flex flex-wrap gap-2" aria-label="Eval results of the last audit">
              {Object.entries(last.results).map(([key, result]) => (
                <li key={key}>
                  <Badge variant={result === "pass" ? "success" : "danger"}>
                    <code className="text-2xs">{key}</code> {result === "pass" ? "pass" : "fail"}
                  </Badge>
                </li>
              ))}
            </ul>
          ) : null}
          {last.state === "failed" ? (
            <Notice tone="warning" data-slot="drift-task">
              A task is open on the supervisor's seat to look into it.
            </Notice>
          ) : null}
          <Table data-slot="drift-history" aria-label="Drift audit history">
            <TableHeader>
              <TableRow>
                <TableHead scope="col">When</TableHead>
                <TableHead scope="col">Why</TableHead>
                <TableHead scope="col">Result</TableHead>
                <TableHead scope="col">Evals</TableHead>
                <TableHead scope="col">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} data-state={a.state}>
                  <TableCell>
                    {formatDateTime(a.ended_at ?? a.started_at ?? a.scheduled_for)}
                  </TableCell>
                  <TableCell>{DRIFT_TRIGGER_LABEL[a.trigger]}</TableCell>
                  <TableCell>
                    <DriftStateBadge audit={a} />
                  </TableCell>
                  <TableCell>{resultsText(a)}</TableCell>
                  <TableCell>{costText(a.cost_micros)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SettingsSection>
  );
}
